import { useState, useEffect, useRef } from 'react';
import { subscribe } from '../../../services/utils/pubsub';
import { ComponentEvents } from '../../../services/wss/events/ComponentEvents';
import topicGroupEngine from './YourNotesGroupManager/TopicGroupEngine';
import groupNodeEngine from './Engines/GroupNodeEngine';
import AppContext from '../../../controllers/AppContext'; // ADDED

// Filter — only handle groupsChanged events from TopicGroupEngine
const isGroupEvent = (e) => e.name === 'TopicGroupEngine' && e.target === 'groupsChanged';

export default function useTopicGroups() {
    const mounted = useRef(false);
    const [groups, setGroups] = useState([]);
    const [activeGroup, setActiveGroup] = useState(null);
    const [loading, setLoading] = useState(false);

    // Updates groups and active group state when engine notifies after fetch or mutation
    const handleEvent = (e) => {
        if (!isGroupEvent(e)) return;
        if (!mounted.current) return;
        setGroups(e.data.groups);
        setActiveGroup(e.data.selected);
        setLoading(false);
    };

    // Async wrapper — useEffect cleanup must be synchronous
    const load = async () => {
        setLoading(true);
        await topicGroupEngine.fetch();
    };

    useEffect(() => {
        mounted.current = true;

        const unsub = subscribe(ComponentEvents.channel, (e) => handleEvent(e));

        if (!topicGroupEngine.hasGroups()) {
            // No cache yet — fetch from BE, result comes via ComponentNotifier
            load();
        } else {
            // Cache already populated — set state directly
            setGroups(topicGroupEngine.getGroups());
            setActiveGroup(topicGroupEngine.selectedGroup());
        }

        return () => {
            mounted.current = false;
            unsub();
        };
    }, []);

    // Trigger GroupNodeEngine when active group changes
    // ADDED — sync active group name into AppContext for CreateNoteDialog default service
    useEffect(() => {
        if (!activeGroup) return;
        groupNodeEngine.selectGroup(activeGroup);
        AppContext.setActiveGroup(activeGroup.name ?? null); // ADDED
    }, [activeGroup]);

    return { groups, activeGroup, setActiveGroup, loading, selectGroup: topicGroupEngine.selectGroup };
}
